import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Redirect, useHistory, useLocation } from 'react-router-dom';
import styles from './Sidebar.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import Logo from 'assets/svg/logo.svg';
import { ReactComponent as ChevronDownIcon } from 'assets/svg/chevron_down.svg';
import { FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { FiChevronLeft, FiChevronRight, FiMenu } from 'react-icons/fi';
import { UserProfile } from 'api/account/models/UserProfile';
import { Reducers } from 'store/types';
import Popover from 'common/components/popover/Popover';
import PopoverItem from 'common/components/popover/PopoverItem';
import { setProfile } from 'store/authentication/action';
import AccountService from 'api/account/AccountService';
import Logger from 'common/services/Logger';
import { ApplicationPaths, LOGGER_LOG_TYPE, STORAGE } from 'Config';
import Avatar from 'common/components/avatar/Avatar';
import AlertsCounter from 'common/components/alertsCounter/AlertsCounter';
import AnnouncementsReopenButton from 'common/components/announcements/AnnouncementsReopenButton';
import { useAnnouncements } from 'common/components/announcements/AnnouncementsProvider';
import BadgeCounter from 'common/components/badgeCounter/BadgeCounter';
import SmsWarningIcon from 'common/components/smsWarningIcon/SmsWarningIcon';
import Storage from 'common/services/Storage';

export interface SidebarItem {
    text: string;
    url?: string;
    badgeValue?: number;
    badgeIcon?: boolean;
    icon?: React.ReactNode;
    children?: SidebarItem[];
}

type Props = {
    items: SidebarItem[];
    collapsed: boolean;
    onToggleCollapse: () => void;
}

const SMS_STALE_REFRESH_MS = 15 * 60 * 1000;

const Sidebar: React.FunctionComponent<Props> = ({ items, collapsed, onToggleCollapse }: Props) => {
    const { t } = useTranslation();
    const { dismissedActiveCount } = useAnnouncements();
    const hasReopenIcon = dismissedActiveCount > 0;
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const user = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const userRefreshToken = useSelector<Reducers, number>(state => state.authentication.refreshToken);
    const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
    const [hovered, setHovered] = useState(false);
    const [textVisible, setTextVisible] = useState(!collapsed);

    const isExpanded = !collapsed || hovered;

    // Delay text appearance so it shows after the width transition is mostly done
    useEffect(() => {
        if (isExpanded) {
            const timer = setTimeout(() => setTextVisible(true), 180);
            return () => clearTimeout(timer);
        } else {
            setTextVisible(false);
        }
    }, [isExpanded]);

    const isActive = (path: any, match: any, loc: any) => !!(match || path === loc.pathname);

    const toggleGroup = (groupName: string) => {
        setOpenGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupName)) {
                next.delete(groupName);
            } else {
                next.add(groupName);
            }
            return next;
        });
    };

    const isGroupActive = (children: SidebarItem[]) => {
        return children.some(child => {
            if (!child.url) return false;
            return location.pathname === child.url || location.pathname.startsWith(child.url + '/');
        });
    };

    // Auto-open only the group containing the active item, close others
    useEffect(() => {
        const activeGroupName = items.find(item => item.children && isGroupActive(item.children))?.text;
        if (activeGroupName) {
            setOpenGroups(new Set([activeGroupName]));
        } else {
            setOpenGroups(new Set());
        }
    }, [location.pathname]);

    const onLogoutClick = async () => {
        const token = await Storage.get(STORAGE.AUTH_USER_TOKEN);
        if (token){
            history.push(`${ApplicationPaths.LogOut}`);
        }
        else {
            window.location.reload();
        }
    }

    const onAccountClick = () => {
        history.push(`/account/details`);
    }

    const getProfile = async () => {
        try {
            if (!user) {
                return;
            }
            const profile = await AccountService.profile();
            dispatch(setProfile(profile));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get user profile`, error);
        }
    }

    useEffect(() => {
        getProfile();
    }, [userRefreshToken])

    useEffect(() => {
        if (!user?.hasSmsPackage) return;
        const id = setTimeout(() => { getProfile(); }, SMS_STALE_REFRESH_MS);
        return () => clearTimeout(id);
    }, [user])

    if (!user) {
        return <Redirect to="/login" />
    }

    const containerClassName = [
        styles.container,
        collapsed ? styles.collapsed : '',
        collapsed && hovered ? styles.hoveredExpanded : '',
    ].filter(Boolean).join(' ');

    return (
        <div
            className={containerClassName}
            onMouseEnter={() => collapsed && setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div className={styles.innerContainer}>
                {/* Toggle button */}
                <div
                    className={styles.toggleButton}
                    onClick={e => { e.stopPropagation(); onToggleCollapse(); }}
                    title={collapsed ? t('menu.expand_sidebar') as string : t('menu.collapse_sidebar') as string}
                >
                    {collapsed ? <FiChevronRight /> : <FiChevronLeft />}
                </div>

                <div className={styles.logoContainer}>
                    <div className={styles.logoContainerWrapper}>
                        <NavLink to={'/'}>
                            {textVisible
                                ? <img src={Logo} className={styles.logoFull} />
                                : <FiMenu className={styles.logoMiniIcon} />
                            }
                        </NavLink>
                    </div>
                </div>
                <div className={styles.itemsContainer}>
                    {items.map((item, i) => (
                        item.children ? (
                            <div key={i} className={styles.group}>
                                <div
                                    className={`${styles.groupHeader} ${item.children && isGroupActive(item.children) ? styles.groupHeaderActive : ''}`}
                                    onClick={() => isExpanded && toggleGroup(item.text)}
                                >
                                    {item.icon && <span className={styles.itemIcon}>{item.icon}</span>}
                                    {textVisible && <span className={styles.groupText}>{item.text}</span>}
                                    {textVisible && (openGroups.has(item.text) ? <FaChevronDown className={styles.groupChevron} /> : <FaChevronRight className={styles.groupChevron} />)}
                                </div>
                                {textVisible && openGroups.has(item.text) && (
                                    <div className={styles.groupItems}>
                                        {item.children.map((child, j) => (
                                            <NavLink
                                                key={j}
                                                className={styles.item}
                                                strict={false}
                                                to={child.url || ''}
                                                activeClassName={styles.itemActive}
                                                isActive={isActive.bind(this, child.url)}
                                                exact={child.url === '/' ? true : false}
                                                title={child.text}
                                            >
                                                <span className={styles.itemText}>
                                                    {child.text}
                                                    {child.badgeValue && (child.badgeIcon !== false
                                                        ? <AlertsCounter totalAlerts={child.badgeValue} className={styles.floatingBadge} size={'small'} />
                                                        : <BadgeCounter total={child.badgeValue} className={styles.floatingBadge} size={'small'} />
                                                    )}
                                                </span>
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <NavLink
                                key={i}
                                className={styles.item}
                                strict={false}
                                to={item.url || ''}
                                activeClassName={styles.itemActive}
                                isActive={isActive.bind(this, item.url)}
                                exact={item.url === '/' ? true : false}
                                title={item.text}
                            >
                                {item.icon && <span className={styles.itemIcon}>{item.icon}</span>}
                                {textVisible && <span className={styles.itemText}>
                                    {item.text}
                                    {item.badgeValue && (item.badgeIcon !== false
                                        ? <AlertsCounter totalAlerts={item.badgeValue} className={styles.floatingBadge} size={'small'} />
                                        : <BadgeCounter total={item.badgeValue} className={styles.floatingBadge} size={'small'} />
                                    )}
                                </span>}
                            </NavLink>
                        )
                    ))}
                </div>
                <div className={styles.bottom}>
                    <Popover
                        align={'center'}
                        positions={['top']}
                        contentContainerClassName={styles.popoverContainer}
                        containerStyle={{ zIndex: '1100' }}
                        content={setIsPopoverOpen => <>
                            <PopoverItem onClick={() => { setIsPopoverOpen(false); onAccountClick(); }}>{t('menu.my_profile')}</PopoverItem>
                            <PopoverItem onClick={onLogoutClick}>{t('menu.logout')}</PopoverItem>
                        </>}
                    >
                        {(_, setIsPopoverOpen) => (
                            <div className={styles.profileItem} onClick={() => setIsPopoverOpen(true)}>
                                <Avatar src={user.mediaUrl} className={styles.profileAvatar} size="x4" />
                                {textVisible && <div className={styles.profileInfo}>
                                    <div className={styles.profileName}>
                                        <span>{user?.realName}</span>
                                        <SmsWarningIcon />
                                        <ChevronDownIcon className={styles.profileChevron} />
                                    </div>
                                </div>}
                            </div>
                        )}
                    </Popover>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: hasReopenIcon ? 'space-between' : 'center', gap: '0.5rem', width: '100%' }}>
                        {textVisible && <div className={styles.copyright}>
                            <p>{t('menu.copyright', { year: new Date().getFullYear() })}</p>
                            <p>{t('menu.all_rights_reserved')}</p>
                        </div>}
                        <AnnouncementsReopenButton />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Sidebar;
